/**
 * 建品草稿（对齐 H5 `create-edits/composables/useDraft.ts`）：
 * 仅在「新建商品」场景把表单暂存到 localStorage，避免误关闭 / 刷新后内容丢失；
 * 编辑态不读写（H5 `isCreateScene` 同样排除编辑态，且带 itemCode / skuId 的路由也不恢复）。
 *
 * 存的是表单快照本身而非 `buildDetailFromForm` 的结果：
 * 详情往返会丢掉「推荐规格合并」标记（merged / hqSkuId）并重置规格 id，草稿要保真。
 */
import type { ProductEditForm } from "./productEditModel";

/** 草稿存储 key（与 H5 保持一致，便于排查） */
export const PRODUCT_DRAFT_KEY = "__product_draft__";
/** 草稿结构版本：表单结构变更后旧草稿直接丢弃，避免脏数据回填 */
export const PRODUCT_DRAFT_VERSION = 1;

export interface ProductDraft {
    version: number;
    /** 新建态表单快照（localSpuId 恒为 null） */
    form: ProductEditForm;
    timestamp: number;
}

/**
 * 读取草稿。解析失败 / 版本不符 / 误存的编辑数据一律返回 null 并清除，
 * 避免脏数据回填后难以排查（对齐 H5 `checkAndRestoreDraft` 的兜底）。
 */
export const readProductDraft = (): ProductDraft | null => {
    let draft: ProductDraft | null = null;
    try {
        const raw = localStorage.getItem(PRODUCT_DRAFT_KEY);
        if (!raw) {
            return null;
        }
        draft = JSON.parse(raw) as ProductDraft | null;
    } catch {
        // 解析失败时兜底返回 null，避免阻断主流程
        draft = null;
    }
    const valid = !!draft
        && draft.version === PRODUCT_DRAFT_VERSION
        && !!draft.form
        && typeof draft.form === "object"
        && Array.isArray(draft.form.specs)
        && Array.isArray(draft.form.platforms)
        // 误存的编辑数据不作为新建草稿恢复
        && draft.form.localSpuId == null;
    if (!valid) {
        clearProductDraft();
        return null;
    }
    return draft;
};

/** 写入草稿；超出配额等异常不阻断建品流程 */
export const saveProductDraft = (form: ProductEditForm) => {
    try {
        const draft: ProductDraft = {
            version: PRODUCT_DRAFT_VERSION,
            form: JSON.parse(JSON.stringify(form)) as ProductEditForm,
            timestamp: Date.now()
        };
        localStorage.setItem(PRODUCT_DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
        console.error("保存建品草稿失败:", error);
    }
};

/** 清除草稿 */
export const clearProductDraft = () => {
    try {
        localStorage.removeItem(PRODUCT_DRAFT_KEY);
    } catch (error) {
        console.error("清除建品草稿失败:", error);
    }
};
